import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import { omitBy } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetCodTransactionsByAdminQuery,
  useGetCodTransactionsQuery,
} from "src/_api/mapitShipInvoice";
import DataTable from "src/components/common/DataTables/DataTable";
import CodTransactionsFilter from "./CodTransactionsFilter";
import { useSelector } from "react-redux";
import { NoteTargetType, ROLES } from "src/constants";
import WithdrawalRequestDialog from "./WithdrawalRequestDialog";
import { Note, Visibility } from "@mui/icons-material";
import CodTransactionOrdersDialog from "./CodTransactionOrdersDialog";
import { NoteDialog, UserAccountNavigate } from "src/_modules/common";
import { useCreateNoteMutation } from "src/_api/noteApi";

const $COLUMN_WIDTH = 200;

const COD_TRANSACTION_STATUSES = {
  PENDING: "PENDING",
  COMPLETED: "COMPLETED",
  CANCELED: "CANCELED",
};

export default function CodTransactionsList({ isWithdrawalRequests = false }) {
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.entities.profile);

  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [queryParams, setQueryParams] = useState({ page: 1, limit: 20 }); // Separate query params

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  //****************************** NOT DAILOG ***************************** */
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [selectedNoteRow, setSelectedNoteRow] = useState(null);
  const handleOpenDialog = () => {
    setOpenNotesDialog(true);
  };
  const handleCloseNotesDialog = () => {
    setOpenNotesDialog(false);
  };
  //******************************************************************************* */

  const handleChange = ({ target: { name, value } }) => {
    setParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const {
    data: merchantTransactions,
    isLoading: merchantTransactionsLoading,
    isFetching: merchantTransactionsFetching,
  } = useGetCodTransactionsQuery(
    omitBy(queryParams, (v) => !v),
    { skip: !!profile?.role ? !(profile?.role === "MERCHANT") : true },
  );

  const {
    data: adminTransactions,
    isLoading: adminTransactionsLoading,
    isFetching: adminTransactionsFetching,
  } = useGetCodTransactionsByAdminQuery(
    omitBy(queryParams, (v) => !v),
    { skip: profile?.role !== ROLES.admin },
  );

  const [transactions, setTransactions] = useState({});

  useEffect(() => {
    if (profile?.role === "MERCHANT" && merchantTransactions) {
      setTransactions(merchantTransactions);
    } else if (profile?.role === ROLES.admin && adminTransactions) {
      setTransactions(adminTransactions);
    }
  }, [merchantTransactions, adminTransactions, profile]);

  const getTransactionsHandler = async (params) => {
    setQueryParams(params);
  };

  const pageChangeHandler = (page) => {
    handleChange({ target: { name: "page", value: page } });
    getTransactionsHandler({ ...params, page });
  };

  useEffect(() => {
    let payload = { ...params };
    if (isWithdrawalRequests) {
      payload = {
        ...payload,
        category: "WITHDRAWAL",
        currentStatus: COD_TRANSACTION_STATUSES.PENDING,
      };
    }
    getTransactionsHandler(payload);
  }, [isWithdrawalRequests]);

  const transactionsLoading =
    merchantTransactionsLoading || adminTransactionsLoading;
  const transactionsFetching =
    merchantTransactionsFetching || adminTransactionsFetching;

  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    if (!!transactions) return setRowCount(transactions?.totalDocs);
  }, [transactions]);

  const handleRowClick = ({ row }) => {
    setSelectedTransaction(row);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case COD_TRANSACTION_STATUSES.COMPLETED:
        return "success";
      case COD_TRANSACTION_STATUSES.CANCELED:
        return "error";
      case COD_TRANSACTION_STATUSES.PENDING:
      default:
        return "warning";
    }
  };

  const [openOrdersDialog, setOpenOrdersDialog] = useState(false);
  const [tansactionId, setTransactionId] = useState(null);

  const openCodTransactionsOrdersDialog = (transactionId) => {
    setTransactionId(transactionId);
    setOpenOrdersDialog(true);
  };

  const closeTransactionsOrdersDialog = () => {
    setOpenOrdersDialog(false);
    setTransactionId(null);
  };

  const columns = [
    {
      field: "merchant",
      renderCell: ({ value }) => <UserAccountNavigate user={value} />,
      sortable: false,
      headerName: t("Merchant"),
      width: $COLUMN_WIDTH * 1.1,
    },
    {
      field: "currentStatus",
      renderCell: ({ value }) => (
        <Chip
          label={t(`codTransactionStatus.${value}`)}
          color={getStatusColor(value)}
          variant="outlined"
        />
      ),
      sortable: false,
      headerName: t("Status"),
      width: $COLUMN_WIDTH * 0.7,
    },
    {
      field: "category",
      renderCell: ({ value, row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t(`codTransactionCategory.${value}`)}</Typography>
          {value === "DEPOSIT" && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                openCodTransactionsOrdersDialog(row?.id);
              }}
            >
              <Visibility />
            </IconButton>
          )}
        </Box>
      ),
      sortable: false,
      headerName: t("Category"),
      width: $COLUMN_WIDTH * 1.1,
    },
    {
      field: "codAmount",
      renderCell: ({ value }) => (
        <Tooltip placement="top" title={value ? value.toFixed(2) : "-"}>
          <Typography variant="body2">
            {value ? value.toFixed(2) : "-"}
          </Typography>
        </Tooltip>
      ),
      sortable: false,
      headerName: t("Amount"),
      width: $COLUMN_WIDTH * 0.8,
    },
    {
      field: "currentCodBalance",
      renderCell: ({ value }) => (
        <Tooltip placement="top" title={value ? value.toFixed(2) : "-"}>
          <Typography variant="body2">
            {value ? value.toFixed(2) : "-"}
          </Typography>
        </Tooltip>
      ),
      sortable: false,
      headerName: t("Remaining COD Balance"),
      width: $COLUMN_WIDTH * 0.8,
    },
    {
      field: "notes",
     renderCell: (params) => {
  console.log("params", params);
  return (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();
        setSelectedNoteRow(params.row);
        handleOpenDialog();
      }}
    >
      <Note sx={{ color: params.row?.hasNotes ? "primary.main" : undefined }} />
    </IconButton>
  );
},
      headerName: t("Notes"),
      width: $COLUMN_WIDTH * 0.7,
    },
    {
      field: "createdAt",
      headerName: t("Created At"),
      width: $COLUMN_WIDTH * 0.9,
      valueGetter: ({ value }) =>
        value ? moment(value).format("DD/MM/YY - h:mm A") : "",
      align: "center",
    },
  ].filter((col) => {
    if (profile?.role !== ROLES.admin) {
      return col.field !== "merchant";
    }
    return true;
  });

  return (
    <Stack gap={2}>
      <NoteDialog
        open={openNotesDialog}
        onClose={handleCloseNotesDialog}
        target={selectedNoteRow?.id}
        targetType={NoteTargetType.CODTRANSACTION}
        name={selectedNoteRow?.merchant?.companyName}
      />
      {!isWithdrawalRequests && (
        <CodTransactionsFilter
          params={params}
          handleChange={handleChange}
          setParams={setParams}
          onSubmit={() => {
            setParams((prev) => ({ ...prev, page: 1 }));
            getTransactionsHandler({ ...params, page: 1 });
          }}
          isLoading={transactionsLoading || transactionsFetching}
        />
      )}
      <CodTransactionOrdersDialog
        open={openOrdersDialog}
        onClose={closeTransactionsOrdersDialog}
        transactionId={tansactionId}
      />
      <Paper
        sx={{
          height: isWithdrawalRequests ? "80vh" : { xs: "67vh", md: "62vh" },
        }}
      >
        <DataTable
          rows={transactions?.docs ?? []}
          columns={columns}
          loading={transactionsLoading || transactionsFetching}
          rowCount={rowCount}
          page={params?.page}
          pageSize={20}
          onRowClick={handleRowClick}
          onPageChange={pageChangeHandler}
          checkboxSelection={false}
          customComponents={["Pagination"]}
        />
      </Paper>
      <WithdrawalRequestDialog
        open={openDialog}
        onClose={handleDialogClose}
        transaction={selectedTransaction}
      />
    </Stack>
  );
}
